import React, { createContext, useContext, useState, useEffect } from 'react';
import { USER_KEY, API_ROUTES } from './authConfig';
import { API_BASE_URL } from '../api/config';

interface Permission {
  resource: string;
  action: string;
}

interface Role {
  roleId: string;
  name: string;
  description: string;
  isSystem: boolean;
  permissions: Permission[];
}

interface User {
  email: string;
  name?: string;
  id?: string;
  role?: string;
}

interface AuthContextType {
  isAuthenticated: boolean;
  user: User | null;
  permissions: Permission[];
  roles: Role[];
  hasPermission: (resource: string, action: string) => boolean;
  hasRole: (roleName: string) => boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  getAccessToken: () => string | null;
}

const AuthContext = createContext<AuthContextType | null>(null);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [accessToken, setAccessToken] = useState<string | null>(null);

  useEffect(() => {
    const initAuth = async () => {
      const userStr = localStorage.getItem(USER_KEY);
      const storedToken = localStorage.getItem('accessToken');
      const storedPermissions = localStorage.getItem('permissions');
      const storedRoles = localStorage.getItem('roles');
      
      if (userStr && storedToken) {
        try {
          const userData = JSON.parse(userStr);
          setIsAuthenticated(true);
          setUser(userData);
          setAccessToken(storedToken);
          
          if (storedPermissions) {
            setPermissions(JSON.parse(storedPermissions));
          }
          if (storedRoles) {
            setRoles(JSON.parse(storedRoles));
          }
          
          // Fetch updated permissions from /me endpoint
          try {
            // eslint-disable-next-line no-restricted-syntax -- session check must not go through ApiClient's 401-refresh-retry loop (it IS the auth bootstrap)
            const response = await fetch(`${API_BASE_URL}/api/v1/auth/me`, {
              headers: {
                'Authorization': `Bearer ${storedToken}`,
              },
            });
            
            if (response.ok) {
              const data = await response.json();
              if (data.permissions) {
                console.log('Loaded permissions from /me endpoint:', data.permissions);
                setPermissions(data.permissions);
                localStorage.setItem('permissions', JSON.stringify(data.permissions));
              }
              if (data.roles) {
                setRoles(data.roles);
                localStorage.setItem('roles', JSON.stringify(data.roles));
              }
            }
          } catch (error) {
            console.error('Failed to fetch user permissions:', error);
          }
        } catch (error) {
          console.error('Failed to parse user data:', error);
          localStorage.removeItem(USER_KEY);
          localStorage.removeItem('accessToken');
          localStorage.removeItem('permissions');
          localStorage.removeItem('roles');
        }
      }
      setIsLoading(false);
    };

    initAuth();
  }, []);

  // Listen for token refresh events from API client
  useEffect(() => {
    const handleTokenRefresh = (event: Event) => {
      const customEvent = event as CustomEvent<{ accessToken: string }>;
      console.log('[Auth] Token refreshed by API client, syncing state');
      setAccessToken(customEvent.detail.accessToken);
    };

    window.addEventListener('tokenRefreshed', handleTokenRefresh);
    return () => {
      window.removeEventListener('tokenRefreshed', handleTokenRefresh);
    };
  }, []);

  const login = async (email: string, password: string) => {
    try {
      console.log('Making login request to:', `${API_BASE_URL}${API_ROUTES.LOGIN}`);
      // eslint-disable-next-line no-restricted-syntax -- login must not go through ApiClient's 401-refresh-retry loop; a failed login is not an expired session, and retrying it through refresh would loop
      const response = await fetch(`${API_BASE_URL}${API_ROUTES.LOGIN}`, {
        method: 'POST',
        credentials: 'include', 
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Login failed');
      }

      console.log('Login response:', data);
      
      localStorage.setItem(USER_KEY, JSON.stringify(data.user));
      localStorage.setItem('accessToken', data.accessToken);
      setAccessToken(data.accessToken);
      setIsAuthenticated(true);
      setUser(data.user);
      
      if (data.permissions) {
        console.log('Loaded permissions from login:', data.permissions);
        setPermissions(data.permissions);
        localStorage.setItem('permissions', JSON.stringify(data.permissions));
      }
      if (data.roles) {
        setRoles(data.roles);
        localStorage.setItem('roles', JSON.stringify(data.roles));
      }
    } catch (error) {
      console.error('Login failed:', error);
      throw error;
    }
  };

  const logout = async () => {
    try {
      // eslint-disable-next-line no-restricted-syntax -- logout must not go through ApiClient's 401-refresh-retry loop; refreshing a token just to log it out is pointless and could re-authenticate the user
      await fetch(`${API_BASE_URL}${API_ROUTES.LOGOUT}`, {
        method: 'POST',
        credentials: 'include',
      });
    } catch (error) {
      console.error('Logout failed:', error);
    }
    
    localStorage.removeItem(USER_KEY);
    localStorage.removeItem('accessToken');
    localStorage.removeItem('permissions');
    localStorage.removeItem('roles');
    setAccessToken(null);
    setIsAuthenticated(false);
    setUser(null);
    setPermissions([]);
    setRoles([]);
  };

  const getAccessToken = () => {
    return accessToken;
  };

  const hasPermission = (resource: string, action: string): boolean => {
    return permissions.some(p => p.resource === resource && p.action === action);
  };

  const hasRole = (roleName: string): boolean => {
    return roles.some(r => r.name === roleName);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={{ 
      isAuthenticated, 
      user, 
      permissions, 
      roles, 
      hasPermission, 
      hasRole, 
      login, 
      logout, 
      getAccessToken 
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
